// =============================================================================
// BP-05-03: Calendar Frontend - Type Definitions
// =============================================================================

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export enum AppointmentType {
  MEDICAL = 'medical',
  THERAPY = 'therapy',
  SCHOOL = 'school',
  LEGAL = 'legal',
  OTHER = 'other',
}

export enum AppointmentStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  NO_SHOW = 'no_show',
  RESCHEDULED = 'rescheduled',
}

export enum LocationType {
  IN_PERSON = 'in_person',
  TELEHEALTH = 'telehealth',
  HOME = 'home',
  PHONE = 'phone',
}

export enum RecurrenceFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum AttendeeRole {
  ORGANIZER = 'organizer',
  PROVIDER = 'provider',
  FAMILY_MEMBER = 'family_member',
  CAREGIVER = 'caregiver',
  OTHER = 'other',
}

export enum AttendeeStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
}

// -----------------------------------------------------------------------------
// Supporting Types
// -----------------------------------------------------------------------------

export interface PrepChecklistItem {
  id: string;
  task: string;
  completed: boolean;
  completedAt?: string;
}

export interface QuestionItem {
  id: string;
  question: string;
  answer?: string;
  answeredAt?: string;
}

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  byDay?: string[];
  byMonthDay?: number[];
  byMonth?: number[];
  count?: number;
  until?: string;
}

// -----------------------------------------------------------------------------
// Appointment Types
// -----------------------------------------------------------------------------

export interface AppointmentAttendee {
  id: string;
  appointmentId: string;
  userId?: string;
  email: string;
  name: string;
  role: AttendeeRole;
  status: AttendeeStatus;
  notifiedAt?: string;
}

export interface Appointment {
  id: string;
  familyId: string;
  childId?: string;
  createdById: string;
  title: string;
  description?: string;
  appointmentType: AppointmentType;
  providerName?: string;
  providerId?: string;
  locationType: LocationType;
  locationName?: string;
  locationAddress?: string;
  telehealthLink?: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  isAllDay: boolean;
  status: AppointmentStatus;
  isRecurring: boolean;
  recurrenceRule?: string;
  recurrenceEndDate?: string;
  parentAppointmentId?: string;
  reminderMinutes: number[];
  prepChecklist: PrepChecklistItem[];
  questionsToAsk: QuestionItem[];
  notes?: string;
  followUpNeeded: boolean;
  followUpNotes?: string;
  externalCalendarId?: string;
  color?: string;
  createdAt: string;
  updatedAt: string;
  attendees?: AppointmentAttendee[];
  childName?: string;
}

export interface AppointmentSummary {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  appointmentType: AppointmentType;
  status: AppointmentStatus;
  color?: string;
  isAllDay: boolean;
  childName?: string;
  providerName?: string;
  locationType: LocationType;
}

// -----------------------------------------------------------------------------
// Form Types
// -----------------------------------------------------------------------------

export interface AppointmentFormData {
  title: string;
  description?: string;
  appointmentType: AppointmentType;
  childId?: string;
  providerName?: string;
  providerId?: string;
  locationType: LocationType;
  locationName?: string;
  locationAddress?: string;
  telehealthLink?: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  isAllDay: boolean;
  isRecurring: boolean;
  recurrence?: RecurrenceRule;
  reminderMinutes: number[];
  prepChecklist: Omit<PrepChecklistItem, 'id' | 'completedAt'>[];
  questionsToAsk: Omit<QuestionItem, 'id' | 'answeredAt'>[];
  notes?: string;
  color?: string;
  attendeeEmails?: string[];
}

export interface AppointmentUpdateData extends Partial<AppointmentFormData> {
  status?: AppointmentStatus;
  followUpNeeded?: boolean;
  followUpNotes?: string;
}

// -----------------------------------------------------------------------------
// Query & Filter Types
// -----------------------------------------------------------------------------

export interface CalendarDateRange {
  start: string;
  end: string;
}

export interface AppointmentFilters {
  childId?: string;
  appointmentType?: AppointmentType;
  status?: AppointmentStatus;
  providerId?: string;
  startDate?: string;
  endDate?: string;
}

export interface CalendarQueryParams extends CalendarDateRange {
  childId?: string;
  appointmentType?: AppointmentType;
  includeRecurring?: boolean;
}

// -----------------------------------------------------------------------------
// Calendar View Types
// -----------------------------------------------------------------------------

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: AppointmentSummary;
}

export interface CalendarSlot {
  start: Date;
  end: Date;
  action: 'select' | 'click' | 'doubleClick';
}

// -----------------------------------------------------------------------------
// API Response Types
// -----------------------------------------------------------------------------

export interface AppointmentListResponse {
  items: AppointmentSummary[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface CalendarDayResponse {
  date: string;
  appointments: AppointmentSummary[];
}

export interface CalendarWeekResponse {
  startDate: string;
  endDate: string;
  days: CalendarDayResponse[];
}

export interface CalendarMonthResponse {
  year: number;
  month: number;
  weeks: CalendarWeekResponse[];
}

export interface AgendaResponse {
  appointments: AppointmentSummary[];
  nextCursor?: string;
}

// -----------------------------------------------------------------------------
// Recurrence Types
// -----------------------------------------------------------------------------

export interface RecurrenceEditScope {
  scope: 'this' | 'thisAndFuture' | 'all';
  instanceDate?: string;
}

export interface RecurrenceDeleteScope {
  scope: 'this' | 'all';
  instanceDate?: string;
}

// -----------------------------------------------------------------------------
// Export Types
// -----------------------------------------------------------------------------

export interface CalendarExportOptions {
  format: 'ical' | 'csv';
  dateRange?: CalendarDateRange;
  childId?: string;
  appointmentTypes?: AppointmentType[];
}

export interface ConflictCheckResult {
  hasConflict: boolean;
  conflictingAppointments: AppointmentSummary[];
}

// -----------------------------------------------------------------------------
// Config Types
// -----------------------------------------------------------------------------

export type AppointmentTypeConfig = {
  [key in AppointmentType]: {
    label: string;
    icon: string;
    defaultColor: string;
  };
};

export const APPOINTMENT_TYPE_CONFIG: AppointmentTypeConfig = {
  [AppointmentType.MEDICAL]: { label: 'Medical', icon: 'Stethoscope', defaultColor: '#7A9E7E' },
  [AppointmentType.THERAPY]: { label: 'Therapy', icon: 'Heart', defaultColor: '#6B8DD6' },
  [AppointmentType.SCHOOL]: { label: 'School', icon: 'GraduationCap', defaultColor: '#9B7ED6' },
  [AppointmentType.LEGAL]: { label: 'Legal', icon: 'Scale', defaultColor: '#2B3A4D' },
  [AppointmentType.OTHER]: { label: 'Other', icon: 'Calendar', defaultColor: '#C4785A' },
};

export type LocationTypeConfig = {
  [key in LocationType]: {
    label: string;
    icon: string;
  };
};

export const LOCATION_TYPE_CONFIG: LocationTypeConfig = {
  [LocationType.IN_PERSON]: { label: 'In Person', icon: 'MapPin' },
  [LocationType.TELEHEALTH]: { label: 'Telehealth', icon: 'Video' },
  [LocationType.HOME]: { label: 'Home Visit', icon: 'Home' },
  [LocationType.PHONE]: { label: 'Phone Call', icon: 'Phone' },
};

export enum CalendarView {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  AGENDA = 'agenda',
}

export const CalendarViewOptions = {
  DAY: 'day',
  WEEK: 'week', 
  MONTH: 'month',
  AGENDA: 'agenda',
} as const;

export interface ReminderPreset {
  label: string;
  minutes: number;
}

export const REMINDER_PRESETS: ReminderPreset[] = [
  { label: '5 minutes before', minutes: 5 },
  { label: '15 minutes before', minutes: 15 },
  { label: '30 minutes before', minutes: 30 },
  { label: '1 hour before', minutes: 60 },
  { label: '2 hours before', minutes: 120 },
  { label: '1 day before', minutes: 1440 },
  { label: '1 week before', minutes: 10080 },
];

export const APPOINTMENT_COLORS: Record<AppointmentType, string> = {
  [AppointmentType.MEDICAL]: '#3B82F6',
  [AppointmentType.THERAPY]: '#8B5CF6',
  [AppointmentType.EDUCATION]: '#10B981',
  [AppointmentType.LEGAL]: '#F59E0B',
  [AppointmentType.EVALUATION]: '#EC4899',
  [AppointmentType.FOLLOW_UP]: '#6366F1',
  [AppointmentType.CONSULTATION]: '#14B8A6',
  [AppointmentType.OTHER]: '#6B7280',
};
